import { useBlockchainConfig } from "@/app/blockchains/filters/blockchain-filter"
import { MIN_PRICE } from "@/app/price/price-input"
import { BalanceMoleculeReatom } from "@/app/profiles/balance/balance-molecule.reatom"
import { useWrappedBalance } from "@/app/wallet-connect/hooks"
import type { Currency, PriceValue } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import { useSuspenseQuery } from "@tanstack/react-query"
import Big from "big.js"
import { useMolecule } from "bunshi/react"
import { useMemo } from "react"

interface Params {
  price: number | undefined
  quantity: number
  currency: Currency
  /**
   * Collection floor, when the caller has one. Only used for the testnet
   * "within 75% of floor" rule — item offers pass it as undefined.
   */
  floor?: PriceValue
}

export const useOfferValidation = ({
  price,
  quantity,
  currency,
  floor,
}: Params) => {
  const { nativeBalanceQueryParamsAtom, blockchain } = useMolecule(
    BalanceMoleculeReatom,
  )
  const wrappedBalance = useWrappedBalance()
  const queryParams = nativeBalanceQueryParamsAtom()
  const { data: balance } = useSuspenseQuery(queryParams)
  const balanceValue = balance as string
  const blockchainConfig = useBlockchainConfig(blockchain)
  const isTestnet = blockchainConfig?.isTestnet ?? false

  const insufficienBalance = useMemo(() => {
    if (price === undefined || !balanceValue) return false
    if (!Number.isFinite(price)) return false

    try {
      const totalPrice = Big(price).mul(quantity)
      const sumBalance = Big(balanceValue).add(wrappedBalance ?? 0)
      return totalPrice.gt(sumBalance)
    } catch {
      return false
    }
  }, [balanceValue, price, quantity, wrappedBalance])

  const minPriceTestnet = useMemo(() => {
    if (!floor) return undefined

    const floorPrice = Big(floor.amount)
    const minPrice = floorPrice.mul(0.75)

    return minPrice.toString()
  }, [floor])

  const error = useMemo(() => {
    if (insufficienBalance) {
      return (
        <Trans>
          Insufficient {currency.abbreviation.toUpperCase()} balance
        </Trans>
      )
    }

    if (!price) return null

    if (price < 0) return <Trans>Enter valid price</Trans>

    if (isTestnet && minPriceTestnet && Big(price).lt(minPriceTestnet)) {
      return (
        <div className="flex flex-col gap-y-1">
          <span>
            <Trans>Price should be within 75% of the floor price</Trans>
          </span>
          <span>
            <Trans>
              At least {minPriceTestnet} {currency.abbreviation}
            </Trans>
          </span>
        </div>
      )
    } else if (!isTestnet && MIN_PRICE && Big(price).lt(MIN_PRICE)) {
      return (
        <div className="flex flex-col gap-y-1">
          <span>
            <Trans>
              Price must be greater than {MIN_PRICE} {currency.abbreviation}
            </Trans>
          </span>
        </div>
      )
    }

    return undefined
  }, [insufficienBalance, price, currency, minPriceTestnet, isTestnet])

  return { error }
}
